import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import {
  Loader2, LogOut, RefreshCw, FileImage, CheckCircle2, Clock, ChevronDown, ChevronUp, ShieldCheck,
  Download, Sun, Moon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useTheme } from "@/hooks/useTheme";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "gc_admin_token";

const Detail = ({ label, value }) => (
  <div>
    <p className="text-[11px] uppercase tracking-wider text-white/40">{label}</p>
    <p className="mt-0.5 text-sm text-white/90">{value || "—"}</p>
  </div>
);

export default function AdminPage() {
  const [theme, toggleTheme] = useTheme();
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [registrations, setRegistrations] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [expanded, setExpanded] = useState({});
  const [updating, setUpdating] = useState("");

  const fetchRegistrations = useCallback(async (tok) => {
    try {
      const res = await axios.get(`${API}/admin/registrations`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      setRegistrations(res.data.registrations);
      setLoadError("");
    } catch (err) {
      if (err.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
      } else {
        setLoadError("Could not load registrations. Please try again.");
      }
    }
  }, []);

  useEffect(() => {
    if (token) fetchRegistrations(token);
  }, [token, fetchRegistrations]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoggingIn(true);
    setLoginError("");
    try {
      const res = await axios.post(`${API}/admin/login`, { email, password });
      sessionStorage.setItem(TOKEN_KEY, res.data.token);
      setToken(res.data.token);
    } catch (err) {
      setLoginError(err.response?.data?.detail || "Login failed");
    } finally {
      setLoggingIn(false);
    }
  };

  const toggleVerified = async (reg) => {
    setUpdating(reg.registration_id);
    try {
      await axios.patch(
        `${API}/admin/registrations/${reg.registration_id}`,
        { payment_verified: !reg.payment_verified },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRegistrations((rs) =>
        rs.map((r) => (r.registration_id === reg.registration_id ? { ...r, payment_verified: !r.payment_verified } : r))
      );
    } catch {
      setLoadError("Update failed — please try again.");
    } finally {
      setUpdating("");
    }
  };

  const exportCsv = () => {
    if (!registrations) return;
    const headers = [
      "Reference", "Submitted", "Language", "Full Name", "Gender", "Age", "Date of Birth",
      "Mobile", "Email", "Address", "State", "Country", "Church / Organization", "Designation",
      "Reference / Heard via", "Attending as family", "Family members", "Payment completed",
      "Payment verified", "From outside Bangalore", "Needs accommodation", "Accommodation option", "Stay proof uploaded", "Arrival", "Departure",
      "Team email sent",
    ];
    const rows = registrations.map((r) => [
      r.registration_id,
      r.created_at ? new Date(r.created_at).toLocaleString() : "",
      r.language === "kn" ? "Kannada" : "English",
      r.personal?.full_name, r.personal?.gender, r.personal?.age, r.personal?.dob,
      r.personal?.mobile, r.personal?.email, r.personal?.address, r.personal?.state, r.personal?.country,
      r.church?.church_name,
      r.church?.designation === "Other" ? `Other: ${r.church?.designation_other || ""}` : r.church?.designation,
      r.church?.reference,
      r.family?.attending_as_family,
      (r.family?.members || []).map((m) => `${m.name} (${m.relation}, ${m.age}, ${m.gender})`).join("; "),
      r.payment?.payment_completed,
      r.payment_verified ? "verified" : "pending",
      r.travel?.from_outside, r.travel?.needs_accommodation,
      r.travel?.needs_accommodation === "yes"
        ? (r.travel?.accommodation_type === "dormitory" ? "In-Campus Dormitory" : r.travel?.accommodation_type === "hotel" ? "Outside Hotel/Airbnb" : "")
        : "",
      r.payment?.stay_proof_path ? "yes" : "no",
      (r.travel?.arrival || "").replace("T", " "), (r.travel?.departure || "").replace("T", " "),
      r.email_sent ? "yes" : "no",
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(","))
      .join("\r\n");
    const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `glory-conference-registrations-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const viewProof = async (path) => {
    const fname = path?.split("/").pop();
    if (!fname) return;
    try {
      const res = await axios.get(`${API}/admin/payment-proof/${fname}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: res.headers["content-type"] }));
      window.open(url, "_blank");
    } catch {
      setLoadError("Could not open the payment proof.");
    }
  };

  /* ---------------- login ---------------- */
  if (!token) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B1233] px-4">
        <form data-testid="admin-login-form" onSubmit={handleLogin}
          className="w-full max-w-sm rounded-3xl border border-white/10 bg-white/[0.04] p-8">
          <ShieldCheck className="mx-auto h-10 w-10 text-[#FFE500]" />
          <h1 data-testid="admin-login-title" className="mt-4 text-center font-display text-2xl font-bold uppercase tracking-wide text-white">
            Team Sign In
          </h1>
          <p className="mt-1 text-center text-xs text-white/40">Glory Conference Bangalore — Registrations</p>
          <div className="mt-7 space-y-4">
            <div>
              <Label className="text-xs text-white/60">Email</Label>
              <Input data-testid="admin-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1 h-11 border-white/15 bg-white/5 text-white" autoComplete="username" />
            </div>
            <div>
              <Label className="text-xs text-white/60">Password</Label>
              <Input data-testid="admin-password-input" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1 h-11 border-white/15 bg-white/5 text-white" autoComplete="current-password" />
            </div>
            {loginError && <p data-testid="admin-login-error" className="text-sm text-red-400">{loginError}</p>}
            <Button data-testid="admin-login-btn" type="submit" disabled={loggingIn}
              className="h-11 w-full rounded-full bg-[#FFE500] font-display font-bold uppercase tracking-wider text-[#0B1233] hover:bg-[#FFE500]/90">
              {loggingIn ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
            </Button>
          </div>
        </form>
      </div>
    );
  }

  /* ---------------- dashboard ---------------- */
  return (
    <div className="min-h-screen bg-[#0B1233] px-4 py-8 sm:px-6">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 data-testid="admin-title" className="font-display text-2xl font-bold uppercase tracking-wide text-white sm:text-3xl">
              Registrations
            </h1>
            <p data-testid="admin-count" className="mt-1 text-sm text-[#FFE500]">
              {registrations ? `${registrations.length} total` : "Loading…"}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button data-testid="admin-theme-toggle" variant="outline" onClick={toggleTheme}
              aria-label="Toggle theme"
              className="rounded-full border-white/20 bg-transparent px-3 text-white hover:border-[#FFE500]/50 hover:text-[#FFE500]">
              {theme === "light" ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
            </Button>
            <Button data-testid="export-csv-btn" variant="outline" onClick={exportCsv}
              disabled={!registrations || registrations.length === 0}
              className="rounded-full border-[#FFE500]/40 bg-transparent text-[#FFE500] hover:bg-[#FFE500] hover:text-[#0B1233]">
              <Download className="mr-1 h-4 w-4" /> Export CSV
            </Button>
            <Button data-testid="admin-refresh-btn" variant="outline" onClick={() => fetchRegistrations(token)}
              className="rounded-full border-white/20 bg-transparent text-white hover:border-[#FFE500]/50 hover:text-[#FFE500]">
              <RefreshCw className="mr-1 h-4 w-4" /> Refresh
            </Button>
            <Button data-testid="admin-logout-btn" variant="outline"
              onClick={() => { sessionStorage.removeItem(TOKEN_KEY); setToken(""); }}
              className="rounded-full border-white/20 bg-transparent text-white hover:border-red-400 hover:text-red-400">
              <LogOut className="mr-1 h-4 w-4" /> Sign Out
            </Button>
          </div>
        </div>

        {loadError && <p data-testid="admin-load-error" className="mt-4 rounded-xl border border-red-400/50 bg-red-400/10 p-3 text-sm text-red-300">{loadError}</p>}

        {!registrations && (
          <div className="mt-16 flex justify-center"><Loader2 data-testid="admin-loading" className="h-8 w-8 animate-spin text-[#FFE500]" /></div>
        )}

        {registrations && registrations.length === 0 && (
          <p data-testid="admin-empty" className="mt-16 text-center text-white/50">No registrations yet.</p>
        )}

        <div className="mt-6 space-y-4">
          {registrations?.map((reg) => {
            const isOpen = expanded[reg.registration_id];
            return (
              <div key={reg.registration_id} data-testid={`reg-card-${reg.registration_id}`}
                className="rounded-2xl border border-white/10 bg-white/[0.03]">
                <button type="button" data-testid={`reg-toggle-${reg.registration_id}`}
                  onClick={() => setExpanded((x) => ({ ...x, [reg.registration_id]: !isOpen }))}
                  className="flex w-full flex-wrap items-center justify-between gap-3 p-5 text-left">
                  <div>
                    <p className="font-display text-lg font-bold tracking-wide text-[#FFE500]">{reg.registration_id}</p>
                    <p className="mt-0.5 text-sm text-white/85">{reg.personal.full_name} · {reg.personal.mobile}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span data-testid={`reg-status-${reg.registration_id}`}
                      className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${
                        reg.payment_verified ? "bg-green-400/15 text-green-400" : "bg-[#FFE500]/15 text-[#FFE500]"
                      }`}>
                      {reg.payment_verified ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Clock className="h-3.5 w-3.5" />}
                      {reg.payment_verified ? "Verified" : "Pending"}
                    </span>
                    {isOpen ? <ChevronUp className="h-5 w-5 text-white/40" /> : <ChevronDown className="h-5 w-5 text-white/40" />}
                  </div>
                </button>

                {isOpen && (
                  <div data-testid={`reg-details-${reg.registration_id}`} className="border-t border-white/10 p-5">
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                      <Detail label="Email" value={reg.personal.email} />
                      <Detail label="Gender / Age" value={`${reg.personal.gender} · ${reg.personal.age}`} />
                      <Detail label="Date of Birth" value={reg.personal.dob} />
                      <Detail label="Language" value={reg.language === "kn" ? "Kannada" : "English"} />
                      <Detail label="Address" value={reg.personal.address} />
                      <Detail label="State" value={reg.personal.state} />
                      <Detail label="Country" value={reg.personal.country} />
                      <Detail label="Church / Org" value={reg.church.church_name} />
                      <Detail label="Designation" value={reg.church.designation === "Other" ? `Other: ${reg.church.designation_other}` : reg.church.designation} />
                      <Detail label="Reference" value={reg.church.reference} />
                      <Detail label="Family attending" value={reg.family.attending_as_family} />
                      {reg.family.members?.length > 0 && (
                        <Detail label="Family members" value={reg.family.members.map((m) => `${m.name} (${m.relation}, ${m.age}, ${m.gender})`).join("; ")} />
                      )}
                      <Detail label="Payment completed" value={reg.payment.payment_completed} />
                      <Detail label="From outside Bangalore" value={reg.travel.from_outside} />
                      <Detail label="Needs accommodation" value={reg.travel.needs_accommodation} />
                      <Detail label="Accommodation" value={
                        reg.travel.needs_accommodation === "yes"
                          ? reg.travel.accommodation_type === "dormitory"
                            ? "In-Campus Dormitory (₹1,500/person)"
                            : reg.travel.accommodation_type === "hotel"
                              ? "Outside Hotel / Airbnb"
                              : "—"
                          : "—"
                      } />
                      <Detail label="Arrival" value={reg.travel.arrival?.replace("T", " ")} />
                      <Detail label="Departure" value={reg.travel.departure?.replace("T", " ")} />
                      <Detail label="Submitted" value={new Date(reg.created_at).toLocaleString()} />
                      <Detail label="Team email sent" value={reg.email_sent ? "Yes" : "No"} />
                    </div>
                    <div className="mt-5 flex flex-wrap gap-3">
                      {reg.payment.payment_proof_path && (
                        <Button data-testid={`reg-proof-${reg.registration_id}`} variant="outline"
                          onClick={() => viewProof(reg.payment.payment_proof_path)}
                          className="rounded-full border-[#FFE500]/40 bg-transparent text-[#FFE500] hover:bg-[#FFE500] hover:text-[#0B1233]">
                          <FileImage className="mr-1 h-4 w-4" /> View Payment Proof
                        </Button>
                      )}
                      {reg.payment.stay_proof_path && (
                        <Button data-testid={`reg-stay-proof-${reg.registration_id}`} variant="outline"
                          onClick={() => viewProof(reg.payment.stay_proof_path)}
                          className="rounded-full border-[#FFE500]/40 bg-transparent text-[#FFE500] hover:bg-[#FFE500] hover:text-[#0B1233]">
                          <FileImage className="mr-1 h-4 w-4" /> View Stay Proof
                        </Button>
                      )}
                      <Button data-testid={`reg-verify-${reg.registration_id}`}
                        onClick={() => toggleVerified(reg)}
                        disabled={updating === reg.registration_id}
                        className={`rounded-full font-bold ${
                          reg.payment_verified
                            ? "bg-white/10 text-white hover:bg-white/20"
                            : "bg-[#FFE500] text-[#0B1233] hover:bg-[#FFE500]/90"
                        }`}>
                        {updating === reg.registration_id ? <Loader2 className="h-4 w-4 animate-spin" />
                          : reg.payment_verified ? "Mark as Pending" : "Mark Payment Verified"}
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
